import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type CreateSupportTicketRequest,
  type CreateSupportTicketResponse,
  type ListSupportTicketsResponse,
  type ReplySupportTicketRequest,
  type ReplySupportTicketResponse,
  type SupportTicketAttachment,
  createSupportTicketContract,
  replySupportTicketContract
} from '@bliks-io/contracts';
import { apiClient } from '../client';
import { downloadBlob } from '@/lib/file-utils';
import { fetchSupportAttachment } from '../queries/useSupportTicket';
import { supportTicketKeys } from '../query-keys';

export type CreateSupportTicketVariables = CreateSupportTicketRequest & {
  attachments?: File[];
};

export type ReplySupportTicketVariables = ReplySupportTicketRequest & {
  attachments?: File[];
};

export function useCreateSupportTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    // multipart, so screenshots skip the JSON body limit. Axios sets the
    // boundary itself once it sees a FormData body.
    mutationFn: async ({
      attachments = [],
      ...data
    }: CreateSupportTicketVariables): Promise<CreateSupportTicketResponse> => {
      const formData = new FormData();
      for (const [field, value] of Object.entries(data)) {
        if (value !== undefined) formData.append(field, String(value));
      }
      for (const file of attachments) {
        formData.append('attachments', file, file.name);
      }

      const { data: response } = await apiClient.post<CreateSupportTicketResponse>(
        createSupportTicketContract.path,
        formData,
        { timeout: 60_000 }
      );
      return response;
    },
    // Zammad's search index lags ticket creation by a few seconds, so an
    // immediate refetch would not include the new ticket (and would overwrite
    // it if we both invalidated and patched). Instead, write the known ticket
    // into the cache; the next natural refetch reconciles with the server.
    onSuccess: (response, request) => {
      const now = new Date().toISOString();
      queryClient.setQueryData<ListSupportTicketsResponse>(supportTicketKeys.lists(), (cached) =>
        cached && !cached.tickets.some((ticket) => ticket.number === response.ticketNumber)
          ? {
              tickets: [
                {
                  id: response.ticketId,
                  number: response.ticketNumber,
                  title: request.summary,
                  status: 'received',
                  createdAt: now,
                  updatedAt: now,
                  hasNewReply: false
                },
                ...cached.tickets
              ]
            }
          : cached
      );
    }
  });
}

/**
 * Fetches an attachment through the API client and hands it to the browser.
 *
 * A mutation rather than a query: it is a user-triggered side effect with no
 * cacheable result — holding file bytes in the query cache would pin them in
 * memory for the session.
 */
export function useDownloadSupportAttachment() {
  return useMutation({
    mutationFn: async ({
      ticketId,
      messageId,
      attachment
    }: {
      ticketId: number;
      messageId: number;
      attachment: SupportTicketAttachment;
    }): Promise<void> => {
      const data = await fetchSupportAttachment({
        ticketId,
        messageId,
        attachmentId: attachment.id
      });
      downloadBlob(data, attachment.filename);
    }
  });
}

export function useReplyToSupportTicket(ticketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    // multipart, like a new ticket: screenshots skip the JSON body limit.
    mutationFn: async ({
      attachments = [],
      body
    }: ReplySupportTicketVariables): Promise<ReplySupportTicketResponse> => {
      const formData = new FormData();
      formData.append('body', body);
      for (const file of attachments) {
        formData.append('attachments', file, file.name);
      }
      const { data: response } = await apiClient.post<ReplySupportTicketResponse>(
        replySupportTicketContract.path.replace(':ticketId', String(ticketId)),
        formData,
        { timeout: 60_000 }
      );
      return response;
    },
    // Unlike ticket creation, an article is readable through the ticket
    // endpoint immediately — that path reads the ticket directly rather than
    // going through Zammad's lagging search index — so a refetch is enough
    // and no optimistic entry is needed.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supportTicketKeys.detail(ticketId) });
      void queryClient.invalidateQueries({ queryKey: supportTicketKeys.lists() });
    }
  });
}
